/**
 * capturaInteligente.ts - Tipos para Sistema de Captura Inteligente
 * Bigwa v1.0 - Janeiro 2026
 */

export interface Author {
  firstName: string;
  lastName: string;
}

export type WorkType = 'article' | 'book' | 'chapter' | 'thesis' | 'conference' | 'report' | 'website';

export interface WorkCandidate {
  id: string;
  title: string;
  authors: Author[];
  year: number;
  type: WorkType;
  doi?: string;
  journal?: string;
  publisher?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  abstract?: string;
  url?: string;
  advisor?: string;

  // Metadados de relevância
  relevanceScore: number;
  relevanceReason: string;
  source: 'crossref' | 'openalex' | 'pubmed' | 'arxiv' | 'scielo' | 'bdtd';
  
  // Para deduplicação
  normalizedKey: string;
  
  // Citações (quando disponível)
  citationCount?: number;
}

export interface CapturaResult {
  // Resultado principal
  principal: {
    found: boolean;
    work: WorkCandidate | null;
    confidence: 'high' | 'medium' | 'low';
  };
  
  // Obras relacionadas (ordenadas por relevância)
  relacionadas: WorkCandidate[];
  
  // Metadados da busca
  meta: {
    query: string;
    authorQuery: string;
    yearQuery: number | null;
    sources: ('crossref' | 'openalex' | 'pubmed' | 'arxiv' | 'scielo' | 'bdtd')[];
    timestamp: string;
    totalFound: number;
    cached: boolean;
  };
}

export interface SearchContext {
  originalAuthor: string;
  originalYear: number | null;
  originalTitle: string;
  normalizedKey: string;
  
  // Dados do contexto da biblioteca
  coCitations: string[];
  concepts: string[];
}

export interface SearchProgress {
  step: number;
  total: number;
  message: string;
  percentage: number;
}

export interface MissingWorkInput {
  authors: string[];
  year: number | null;
  title: string;
  normalizedKey: string;
}

// Limites por plano
export interface CapturaLimits {
  capturesPerMonth: number;
  relatedPerSearch: number;
}

export const CAPTURA_LIMITS: Record<string, CapturaLimits> = {
  free: { capturesPerMonth: 5, relatedPerSearch: 5 },
  basico: { capturesPerMonth: 10, relatedPerSearch: 5 },
  premium: { capturesPerMonth: 50, relatedPerSearch: 15 },
  max: { capturesPerMonth: 150, relatedPerSearch: 15 },
};

// Utilitário para normalizar chave de deduplicação
export function normalizeWorkKey(authors: string[], year: number | null, title: string): string {
  const authorPart = authors[0]?.toLowerCase().replace(/[^a-z]/g, '').slice(0, 10) || 'unknown';
  const yearPart = year?.toString() || '0000';
  const titlePart = title?.toLowerCase().replace(/[^a-z]/g, '').slice(0, 20) || 'notitle';
  return `${authorPart}-${yearPart}-${titlePart}`;
}

// Mapear tipo CrossRef para WorkType
export function mapCrossRefType(crossRefType: string): WorkType {
  const typeMap: Record<string, WorkType> = {
    'journal-article': 'article',
    'book': 'book',
    'book-chapter': 'chapter',
    'monograph': 'book',
    'proceedings-article': 'conference',
    'dissertation': 'thesis',
    'report': 'report',
    'posted-content': 'article',
    'reference-entry': 'chapter',
  };
  return typeMap[crossRefType] || 'article';
}

// Mapear tipo OpenAlex para WorkType
export function mapOpenAlexType(openAlexType: string): WorkType {
  const typeMap: Record<string, WorkType> = {
    'article': 'article',
    'book': 'book',
    'book-chapter': 'chapter',
    'dissertation': 'thesis',
    'proceedings-article': 'conference',
    'report': 'report',
    'preprint': 'article',
  };
  return typeMap[openAlexType] || 'article';
}
